import React, { useState, useEffect, useCallback } from 'react';
import { connect } from 'react-redux';
import { getBackend } from '../backend';
import { ListRow, SetupListPanel, SetupDrill, confirmDiscardIfDirty, useReportDirty, formsEqual, isMasterRole, Field, useAutoSelectFirstInLandscape, usePersistedSelection, useSaveSelection, setupPersistedSelection } from './setupCommon';
import { fieldAccess } from './setupAccess';
import { askConfirm } from './confirmService';

/**
 * CloudCustomerSetup — the Dividia-level org (unified-viewer-setup-design.md §4.2, isite
 * admin/forms/customer/CustomerForm.js ported into our Setup aesthetic).
 *
 * A customer is the top of the cloud org tree (customer ▸ site ▸ device). Management is MASTER-only
 * (bRole 1; backend modifyOrCreateCustomer also gates it); every non-master sees it read-only.
 * Fields mirror isite/JSCustomer: Name, File Directory, Domain, Public.
 *
 * Delete is a reversible SOFT delete (backend flips JSCustomer.fDeleted; the customer + its whole
 * subtree drop out of every read but nothing is destroyed). Master-only, behind a danger confirm.
 *
 * LAYOUT — the shared mobile drill-down (SetupDrill): the list is a full-height view; picking a row
 * (or New) swaps to the detail with a ‹ Customers back link, the saved name as the heading, and a
 * sticky-after-content Save/Revert bar. This is the locked template shared by every org screen.
 */

const blankCustomer = () => ({ id: null, name: '', dir: '', domain: '', public: false });

const customerToForm = (c) => ({
  id: c.bID,
  name: c.sName || '',
  dir: c.sDir || '',
  domain: c.sDomain || '',
  public: c.fPublic === 1 || c.fPublic === true,
});

const CloudCustomerSetup = (props) => {
  const [customers, setCustomers] = useState([]);
  const [selectedId, setSelectedId] = useState(null);   // bID, 'new', or null
  const [form, setForm] = useState(null);
  const [pristine, setPristine] = useState(null); // the saved/loaded snapshot; dirty = form differs from it
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [filter, setFilter] = useState('');

  const isMaster = isMasterRole(props.bRole);
  const canManage = isMaster; // action-button gate (New / Delete / Save) — master-only for now

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const { customers: c } = await getBackend().getCustomers({ token: props.sSess });
      setCustomers(c);
      return c;
    } catch (e) {
      setError('Could not load customers. ' + (e && e.message ? e.message : ''));
      return null;
    } finally {
      setLoading(false);
    }
  }, [props.sSess]);

  useEffect(() => { load(); }, [load]);

  // dirty is DERIVED: the live form differs from the pristine snapshot (so undoing edits clears it).
  const dirty = form !== null && !formsEqual(form, pristine);
  const seed = (f) => { setForm(f); setPristine(f); setError(''); setNotice(''); };

  useReportDirty(dirty); // let SetupView guard a top-tab switch away from unsaved edits
  useSaveSelection('customers', selectedId);
  usePersistedSelection('customers', customers, (c) => c.bID, (c) => { setSelectedId(c.bID); seed(customerToForm(c)); });
  const select = async (c) => {
    if (!(await confirmDiscardIfDirty(dirty))) return;
    setSelectedId(c.bID); seed(customerToForm(c));
  };
  const startNew = async () => {
    if (!(await confirmDiscardIfDirty(dirty))) return;
    setSelectedId('new'); seed(blankCustomer());
  };
  // Back out of the detail view to the list (mobile drill-down).
  const closeDetail = async () => {
    if (!(await confirmDiscardIfDirty(dirty))) return;
    setSelectedId(null); seed(null);
  };
  const patch = (p) => { setForm((f) => ({ ...f, ...p })); setNotice(''); };
  // Undo all unsaved edits in place — snap the form back to the pristine snapshot without leaving
  // the detail view (dirty then clears because form === pristine again).
  const revert = () => { setForm(pristine); setError(''); setNotice(''); };

  const save = async () => {
    if (!form.name.trim() || !form.dir.trim() || !form.domain.trim()) {
      setError('Name, File Directory and Domain are required.'); return;
    }
    setBusy(true); setError(''); setNotice('');
    try {
      const { ok } = await getBackend().saveCustomer(form, { token: props.sSess });
      if (!ok) throw new Error('Save was rejected.');
      setNotice(form.id ? 'Customer saved.' : 'Customer created.');
      // Stay on the saved record (re-seeded clean) instead of deselecting to a blank pane. Find it by id
      // (edit) or by name+domain (a fresh create, whose new id the save response doesn't return). (David 2026-08-30)
      const fresh = await load();
      const saved = (fresh || []).find((c) => (form.id ? c.bID === form.id : (c.sName === form.name && c.sDomain === form.domain)));
      if (saved) { setSelectedId(saved.bID); seed(customerToForm(saved)); }
      else { setSelectedId(null); setForm(null); setPristine(null); }
    } catch (e) {
      setError('Could not save. ' + (e && e.message ? e.message : 'You may not have permission.'));
    } finally { setBusy(false); }
  };

  // Soft delete — the customer and everything under it (sites/devices/cameras/users) is hidden
  // everywhere and its users lose access; nothing is destroyed (reversible by an administrator).
  const del = async () => {
    if (!form || !form.id) return;
    if (!(await askConfirm({
      title: 'Delete customer?',
      message: `Delete customer "${form.name}"? Its sites, devices, cameras, and users will be hidden everywhere and its users will lose access.`,
      confirmLabel: 'Delete',
      danger: true,
    }))) return;
    setBusy(true); setError('');
    try {
      const { ok } = await getBackend().deleteCustomer(form.id, { token: props.sSess });
      if (!ok) throw new Error('Delete was rejected.');
      await load(); setSelectedId(null); setForm(null); setPristine(null);
    } catch (e) {
      setError('Could not delete. ' + (e && e.message ? e.message : ''));
    } finally { setBusy(false); }
  };

  const filtered = customers.filter((c) =>
    !filter || (c.sName || '').toLowerCase().includes(filter.toLowerCase()));
  // mobile-landscape split shows list + detail together → auto-select the first row so the detail
  // pane isn't empty on first load (landscape-only; portrait keeps the list-first drill).
  useAutoSelectFirstInLandscape(filtered, selectedId !== null || setupPersistedSelection('customers') != null, select);

  const detailOpen = form !== null;
  // Heading is the SAVED record name (stable while you edit the Name field); "New Customer" for a create.
  const detailTitle = selectedId === 'new'
    ? 'New Customer'
    : ((customers.find((c) => c.bID === selectedId) || {}).sName || 'Customer');

  return (
    <SetupDrill
      detailOpen={detailOpen}
      backLabel="Customers"
      wideList
      title={detailTitle}
      onBack={closeDetail}
      list={(
        <SetupListPanel
          searchFirst
          newLabel="+ New"
          onNew={canManage ? startNew : null}
          filter={filter}
          setFilter={setFilter}
          filterPlaceholder="Filter customers…"
          loading={loading}
          empty={!filtered.length}
          emptyText="No customers."
        >
          {filtered.map((c) => (
            <ListRow
              key={c.bID}
              active={selectedId === c.bID}
              onClick={() => select(c)}
              name={c.sName}
              sub={c.sDomain}
            />
          ))}
        </SetupListPanel>
      )}
      actions={detailOpen && canManage ? (
        <>
          {form.id ? (
            <button className="setup-btn--danger" disabled={busy} onClick={del}>Delete</button>
          ) : null}
          <div className="setup-actions__spacer" />
          {dirty ? (
            <button className="setup-btn--secondary" disabled={busy} onClick={revert}>Revert</button>
          ) : null}
          <button
            className={'setup-btn--save' + (dirty ? '' : ' setup-btn--save-clean')}
            disabled={busy || !dirty} onClick={save}>
            {busy ? 'Saving…' : (form.id ? 'Save' : 'Create Customer')}
          </button>
        </>
      ) : null}
    >
      {detailOpen ? (
        <>
          {/* Every input is the ONE Field — type picks the widget, access (single source) picks the gate. */}
          <Field type="string" label="Name" value={form.name} access={fieldAccess('customer.name')} isMaster={isMaster}
            onChange={(v) => patch({ name: v })} placeholder="Customer name" />
          <Field type="string" label="File Directory" value={form.dir} access={fieldAccess('customer.fileDir')} isMaster={isMaster}
            onChange={(v) => patch({ dir: v })} placeholder="e.g. acme" />
          <Field type="string" label="Domain" value={form.domain} access={fieldAccess('customer.domain')} isMaster={isMaster}
            onChange={(v) => patch({ domain: v })} placeholder="e.g. acme.dividia.net" />
          <Field type="bool" label="Publicly listed" value={form.public} access={fieldAccess('customer.public')} isMaster={isMaster}
            onChange={(v) => patch({ public: v })} />

          {!canManage ? (
            <div className="setup-hint setup-hint--spaced">
              Customer management is available to Dividia master administrators.
            </div>
          ) : null}

          {error ? <div className="setup-error">{error}</div> : null}
          {notice ? <div className="setup-notice">{notice}</div> : null}
        </>
      ) : null}
    </SetupDrill>
  );
};

export { CloudCustomerSetup }; // bare, for unit tests (pass sSess/bRole as props)

const mapStateToProps = (state) => ({
  sSess: state.auth.sSess,
  bRole: state.user.bRole,
});

export default connect(mapStateToProps)(CloudCustomerSetup);
